import { App, Space, SpaceConfig } from '@flatfile/api-internal/api'
import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Icon,
  Input,
  Label,
  Separator,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@flatfile/baseline'
import { useParams } from 'react-router-dom'
import { useCallback, useEffect, useState } from 'react'
import useResource from '../../resources/hooks/useResource'
import { useCreateResource, useUpgradeResource } from '../../resources/hooks/useResourceMutation'
import { useFilteredCollection } from '../../resources/hooks/useCollectionIds'
import { logger } from '../../utils/logging'
import { AppHTTPService } from '../../api/http-service'
import { capitalize, getArticle } from '../../utils/stringUtils'

type CreateSpaceForm = {
  name: string
  namespace: string
}

export function CreateNewSpace(props: {
  entity: string
  environmentId: string
  isProd: boolean
  onSpaceCreated?: (newSpace: Space) => void
  disabled?: boolean
  canReadAgents?: boolean
}) {
  const { entity, environmentId, isProd, onSpaceCreated, disabled, canReadAgents = false } = props
  const [dialogOpen, setDialogOpen] = useState(false)
  const [formValues, setFormValues] = useState<CreateSpaceForm>({
    name: '',
    namespace: '',
  })
  const [formErrors, setFormErrors] = useState<Record<string, string | null>>({
    name: null,
  })
  const [creationError, setCreationError] = useState('')
  const { id = '' } = useParams()
  const { namespace = '' } = useParams()
  const currentAppPredicate = useCallback(
    (app: App) => {
      return app.id === id || app.namespace === namespace
    },
    [id, namespace]
  )
  const currentAppId = useFilteredCollection('APPS', currentAppPredicate)
  const currentApp = useResource('APPS', currentAppId[0])

  // Track the lazy agent lookup separately from space creation so the dialog cannot submit early.
  const [agentDeploymentStatus, setAgentDeploymentStatus] = useState<
    'unknown' | 'loading' | 'has-agents' | 'no-agents'
  >('unknown')

  useEffect(() => {
    let isCurrentRequest = true

    if (!dialogOpen || isProd || !canReadAgents || !environmentId) {
      setAgentDeploymentStatus('unknown')
      return () => {
        isCurrentRequest = false
      }
    }

    setAgentDeploymentStatus('loading')

    AppHTTPService.axios
      .get('/v1/agents', { params: { environmentId } })
      .then((response) => {
        if (!isCurrentRequest) return
        const deployedAgents = response.data?.data ?? []
        setAgentDeploymentStatus(deployedAgents.length > 0 ? 'has-agents' : 'no-agents')
      })
      .catch((error) => {
        if (!isCurrentRequest) return
        logger.error('Error fetching agents for create space dialog', { error })
        setAgentDeploymentStatus('unknown')
      })

    return () => {
      isCurrentRequest = false
    }
  }, [dialogOpen, isProd, canReadAgents, environmentId])

  if (!namespace && id) {
    if (!currentApp) throw new Error('App not found')
  }

  // Set the namespace once we have the currentApp
  useState(() => {
    if (currentApp?.namespace) {
      setFormValues((prev) => ({ ...prev, namespace: currentApp.namespace || '' }))
    }
  })

  const [createSpace, creatingSpace] = useCreateResource('SPACES')
  const [upgradeSpace, upgradingSpace] = useUpgradeResource('SPACES')
  const isCheckingAgentDeployment = agentDeploymentStatus === 'loading'
  const isLoading = creatingSpace || upgradingSpace

  const checkSpaceNameExists = async (name: string): Promise<boolean> => {
    const { data: spaces } = await AppHTTPService.axios
      .get('/v1/internal/spaces', { params: { environmentId, app: currentAppId[0], search: name } })
      .then((response) => response.data)

    return spaces.some((space: Space) => space.name.trim().toLowerCase() === name.toLowerCase())
  }

  const validateForm = async (): Promise<boolean> => {
    const errors: Record<string, string | null> = {}

    // Validate name
    if (!formValues.name.trim()) {
      errors.name = 'Name is required.'
    } else {
      const spaceNameInUse = await checkSpaceNameExists(formValues.name.trim())
      if (spaceNameInUse) {
        errors.name = `${capitalize(getArticle(entity))} ${entity} with this name already exists.`
      }
    }

    setFormErrors(errors)
    return !Object.values(errors).some((error) => error !== null)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormValues((prev) => ({ ...prev, [name]: value }))
    setFormErrors((prev) => ({ ...prev, [name]: null }))
  }

  const handleCreateNewSpace = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isLoading) return

    const isValid = await validateForm()
    if (!isValid) return

    const flatFileConfig: SpaceConfig = {
      name: formValues.name.trim(),
      autoConfigure: true,
      environmentId,
      namespace: currentApp?.namespace ?? '',
      appId: currentApp?.id,
    }

    try {
      const newSpace = await createSpace(flatFileConfig)
      onSpaceCreated?.(newSpace)

      if (currentApp?.type === 'PROJECTS') {
        await upgradeSpace(newSpace.id, { id: 'base' })
      }

      handleClose()
    } catch (error) {
      logger.error('Error creating new Space', { error })
      setCreationError(`Failed to create new ${entity}. Please try again.`)
    }
  }

  const handleClose = () => {
    setDialogOpen(false)
    setFormValues({ name: '', namespace: currentApp?.namespace ?? '' })
    setFormErrors({ name: null })
    setCreationError('')
  }

  return (
    <>
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New {entity}</DialogTitle>
          </DialogHeader>

          {creationError && (
            <Alert variant='destructive' className='mb-4'>
              <AlertDescription>{creationError}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleCreateNewSpace} className='space-y-4'>
            <div>
              <Label htmlFor='name'>Name</Label>
              <div className='relative mt-1'>
                <Input
                  id='name'
                  name='name'
                  value={formValues.name}
                  onChange={handleInputChange}
                  autoFocus
                  disabled={isLoading}
                  hasError={!!formErrors.name}
                />
              </div>
              {formErrors.name && <p className='text-xs text-destructive mt-1'>{formErrors.name}</p>}
            </div>

            {!isProd && canReadAgents && agentDeploymentStatus === 'has-agents' && (
              <div className='mt-4 p-3 border border-border rounded-md'>
                <Text variant='callout' className='text-xs'>
                  This will create a new {entity} with a{' '}
                  <span className='font-bold'>space:{currentApp?.namespace}</span> namespace.
                </Text>
                <Separator className='my-2' />
                <Text variant='footnote' className=' text-xxs'>
                  When a listener is filtering for this specific namespace (or there's a global listener) and is
                  awaiting the <code className='bg-gray-100 px-1 rounded'>ready</code> state of the{' '}
                  <code className='bg-gray-100 px-1 rounded'>space:configure</code> job, it will finalize the creation
                  of this new {entity} within the <span className='font-bold'>{currentApp?.name}</span> App.
                </Text>
              </div>
            )}

            {!isProd && canReadAgents && agentDeploymentStatus === 'no-agents' && (
              <Alert variant='outline'>
                <div className='flex flex-row gap-2 mb-2'>
                  <Icon name='status-warning-triangle' color='destructive'></Icon>
                  <Text variant='callout' emphasized>
                    Warning: No agents deployed in this environment!
                  </Text>
                </div>

                <Text variant='footnote' className='text-sm'>
                  Are you sure you're in the correct environment? This {entity} will not function properly without
                  agents to respond to the events.
                </Text>
              </Alert>
            )}

            <div className='flex justify-end gap-2 pt-2'>
              <Button type='button' variant='outline' onClick={handleClose} disabled={isLoading}>
                Cancel
              </Button>
              <Button
                type='submit'
                variant='primary'
                disabled={isLoading || isCheckingAgentDeployment}
                loading={isLoading}
              >
                Confirm
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {disabled ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className='inline-block'>
              <Button
                disabled={disabled}
                size='sm'
                variant='primary'
                onClick={() => setDialogOpen(true)}
                icon='plus-small'
              >
                Create New {entity}
              </Button>
            </div>
          </TooltipTrigger>
          <TooltipContent side='top' sideOffset={5} className='flex items-center px-2 py-1 max-w-[200px]'>
            <Text variant='body' className='text-sm'>
              This App is not setup to be used in Production Environments.
            </Text>
          </TooltipContent>
        </Tooltip>
      ) : (
        <Button size='md' variant='primary' onClick={() => setDialogOpen(true)} icon='plus-small'>
          Create New {entity}
        </Button>
      )}
    </>
  )
}
